// RGR10 조회조건 저장/복원 — 순수 변환 (index.tsx 배선에서 소비, 테스트 대상)
//
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통
// UX, 13개 화면이 쓰는 기존 방식 — D16). `@/hooks/useSessionPersistedState` 는 쓰지 않는다(D3 훅 지시 폐기).
// 저장 경로는 `MenuStorage.menuOnSession.setSearch` → `Utils.qsStringify`(arrayFormat: comma),
// 복원 경로는 `useParsedQuery`(qs.parse comma) 다 — allInboundStatus/searchQuery.ts 준용.
//
// D2 로 `core.FilterArea` + 외부 antd Form 을 쓰므로 `onFormReady(form)` 로 받은 form 인스턴스에
// `setFieldsValue` 로 값을 심을 통로가 있다(erpInbound 가 겪은 통로 부재 문제가 없다) — 필터 필드
// 6종(조회 기준일 포함)을 전부 저장·복원한다. 상태 탭(ordStatus)·페이지·목록 검색어도 함께 싣는다(D16).
import dayjs from 'dayjs'
import { ALL_STATUS_TAB, STATUS_TABS, StatusTabKey } from './constants'
import { defaultDateRange, FIELD_NAMES, ReturnMonitoringFormValues, toDateType } from './filterToRequest'

const DATE_FORMAT = 'YYYYMMDD'

/** URL·세션에 남는 형태. 값이 있는 키만 싣는다(qsStringify 가 undefined 는 걸러 주지만 빈 배열은 남긴다). */
export interface ReturnMonitoringSearchQuery {
  shipper?: string
  /** 조회 기준일(RTN_DATE·ACT_RETURN_DATE·INSPECT_DATE·GR_ACT_DATE) — 없으면 접수일로 되돌린다. */
  dateType?: string
  dateFrom?: string
  dateTo?: string
  rtnInvoiceNo?: string[]
  actFlag?: string
  ordNo?: string[]
  ordStatus?: string
  /** 1-based 화면 페이지 — 클라 페이징이라 이 값만 복원하면 표가 보던 페이지로 돌아온다. */
  page?: number
  /** 표 위 목록 검색어(확정된 값만 — 타이핑마다 남기지 않는다, ErpInbound 준용). */
  listQuery?: string
}

/** `mfs.FormTextArea` 는 배열/단건 문자열 어느 쪽으로도 온다 — 문자열 목록으로 맞춘다. */
const toList = (v?: string[] | string): string[] | undefined => {
  let list: string[] = []
  if (Array.isArray(v)) list = v
  else if (typeof v === 'string' && v.trim()) list = [v.trim()]
  const cleaned = list.map((x) => String(x).trim()).filter(Boolean)
  return cleaned.length ? cleaned : undefined
}

const toDateString = (v?: dayjs.Dayjs | null): string | undefined => (v?.isValid() ? v.format(DATE_FORMAT) : undefined)

/**
 * 폼 값 + 상태 탭 + 표 상태 → 저장 형태.
 *
 * `isDateTypeEnabled`(`RETURN_MONITORING_DATE_TYPE_FILTER_FLAG`) 가 off 면 `dateType` 키를 아예 싣지
 * 않는다 — URL 도 as-is 그대로여야 한다. 이 함수는 조회·탭 전환 같은 사용자 동작에서만 불려
 * FF 가 이미 도착한 뒤라 늦게 켜지는 경합이 없다. 기본값은 as-is(off) 다.
 */
export const toSearchQuery = (
  values: ReturnMonitoringFormValues,
  activeTab: StatusTabKey,
  page: number,
  listQuery: string,
  isDateTypeEnabled = false,
): ReturnMonitoringSearchQuery => {
  const range = values[FIELD_NAMES.dateRange]
  const [from, to] = Array.isArray(range) ? range : [range, range]

  return {
    shipper: values[FIELD_NAMES.shipper] || undefined,
    dateType: isDateTypeEnabled ? toDateType(values[FIELD_NAMES.dateType]) : undefined,
    dateFrom: toDateString(from),
    dateTo: toDateString(to),
    rtnInvoiceNo: toList(values[FIELD_NAMES.rtnInvoiceNo]),
    actFlag: values[FIELD_NAMES.actFlag] || undefined,
    ordNo: toList(values[FIELD_NAMES.ordNo]),
    ordStatus: activeTab === ALL_STATUS_TAB ? undefined : activeTab,
    page: page > 1 ? page : undefined,
    listQuery: listQuery || undefined,
  }
}

/** 문자열 목록만 남긴다(comma 파서는 값이 하나면 배열이 아니라 문자열을 준다). 조작된 URL 은 버린다. */
const asRestoredList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) {
    const cleaned = v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String)
    return cleaned.length ? cleaned : undefined
  }
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

/** `yyyyMMdd` 문자열 → Dayjs. 형식이 어긋나면 버린다(조작된 URL 이 날짜 칸을 Invalid Date 로 만들지 않게). */
const asRestoredDate = (v: unknown) => {
  if (typeof v !== 'string' || !/^\d{8}$/.test(v)) return null
  const parsed = dayjs(`${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6)}`)
  return parsed.isValid() ? parsed : null
}

const asText = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

const asPositiveInt = (v: unknown, fallback: number): number => {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

const asStatusTab = (v: unknown): StatusTabKey => {
  const found = STATUS_TABS.find((t) => t.key === v)
  return found ? found.key : ALL_STATUS_TAB
}

export interface RestoredReturnMonitoringSearch {
  values: ReturnMonitoringFormValues
  activeTab: StatusTabKey
  page: number
  listQuery: string
}

/**
 * URL 쿼리(파싱 결과) → 복원 조회조건.
 *
 * URL 에 화주명이 실제로 없으면(최초 진입 등) 복원 대상이 아니다(undefined) — 빈 표로 연다
 * (erpInbound `hasQuery` 판정과 동일하게 **원본 쿼리 값**으로 가른다). 화주 고정 계정으로 여는
 * 기본값이나 URL 값 보정은 페이지가 맡는다(`filterInitialValues = restored?.values ??
 * buildDefaultValues(ownShipperCode)`, 화주 고정 계정은 그 뒤 `forceOwnShipper` 로 한 번 더 덮는다) —
 * 여기서 화주코드를 몰라도 되므로 파라미터로 받지 않는다(GR05 `fromSearchQuery` 준용).
 */
export const fromSearchQuery = (query: Record<string, unknown>): RestoredReturnMonitoringSearch | undefined => {
  const shipper = asText(query.shipper)
  if (!shipper) return undefined

  const dateFrom = asRestoredDate(query.dateFrom)
  const dateTo = asRestoredDate(query.dateTo)

  return {
    values: {
      [FIELD_NAMES.shipper]: shipper,
      // FF 와 무관하게 되돌린다 — FF 가 마운트 뒤 늦게 켜져도 URL 이 들고 온 기준일이 살아남아야 한다.
      // off 인 동안은 칸이 렌더되지 않고 요청·URL 양쪽에서 잠기므로 이 값이 새어 나가지 않는다.
      [FIELD_NAMES.dateType]: toDateType(asText(query.dateType)),
      // 양끝이 다 유효할 때만 그대로 쓴다. 없거나 한쪽만 있거나 깨졌으면 기본 기간(최근 일주일)으로 채운다 —
      // 복원 진입은 필수 검사 없이 바로 조회하므로 기간이 비면 전체 기간 조회가 나간다(LOGISPRD-14902).
      [FIELD_NAMES.dateRange]: dateFrom && dateTo ? [dateFrom, dateTo] : defaultDateRange(),
      [FIELD_NAMES.rtnInvoiceNo]: asRestoredList(query.rtnInvoiceNo) ?? [],
      [FIELD_NAMES.actFlag]: asText(query.actFlag),
      [FIELD_NAMES.ordNo]: asRestoredList(query.ordNo) ?? [],
    },
    activeTab: asStatusTab(query.ordStatus),
    page: asPositiveInt(query.page, 1),
    listQuery: asText(query.listQuery) ?? '',
  }
}
